import { isSameMonth, toDate, parseISO } from 'date-fns';
import { MonthMoney, MonthsMap, Prices } from '../model/types';
import { Currency } from 'enums';

export const getProgressLevel = (price: number, maxPrice: number) => {
	if (maxPrice === 0) {
		return 0;
	}

	return Math.round((price / maxPrice) * 100);
};

export const safeAddDays = (date: Date | string, days: number): Date => {
	const newDate = date instanceof Date ? toDate(date) : parseISO(date);

	newDate.setDate(newDate.getDate() + days);

	return newDate;
};

export const safeStartOfMonth = (date: Date | string): Date => {
	const newDate = date instanceof Date ? toDate(date) : parseISO(date);

	newDate.setDate(1);

	return newDate;
};

export const getDaysFromMonth = (initialDate: Date): Date[] => {
	let currentDate = safeStartOfMonth(initialDate);

	const dates: Date[] = [];

	while (isSameMonth(initialDate, currentDate)) {
		dates.push(currentDate);

		currentDate = safeAddDays(currentDate, 1);
	}

	return dates;
};

export const hasOwn = (obj: object, key: string) => Object.prototype.hasOwnProperty.call(obj, key);

export const buildMonthsMap = (prices: Prices): MonthsMap => {
	const months: MonthsMap = {};

	for (const dateISO in prices) {
		if (!hasOwn(prices, dateISO)) {
			continue;
		}

		const monthISO = safeStartOfMonth(dateISO).toISOString();
		const price = prices[dateISO]?.price;

		if (!hasOwn(months, monthISO)) {
			months[monthISO] = { minPrice: null, maxPrice: null };
		}

		if (!price || !Object.keys(price).length) {
			continue;
		}

		const month = months[monthISO];
		const { amount, currency } = price;

		if (!month.minPrice || amount < month.minPrice.amount) {
			month.minPrice = { amount, currency };
		}

		if (!month.maxPrice || amount > month.maxPrice.amount) {
			month.maxPrice = { amount, currency };
		}
	}

	return months;
};

export const getMonthsArray = (monthMap: MonthsMap): MonthMoney[] => {
	const monthsArray: MonthMoney[] = [];

	for (const month in monthMap) {
		if (hasOwn(monthMap, month)) {
			monthsArray.push({
				dateISO: month,
				minPrice: monthMap[month].minPrice
					? {
							amount: monthMap[month].minPrice.amount,
							currency: monthMap[month].minPrice.currency as Currency
					  }
					: null
			});
		}
	}

	return monthsArray;
};

export const getSelectedPrice = (prices: Prices, openedDate: Date) => {
	const iso = openedDate.toISOString();
	const dateOnly = iso.split('T')[0];
	const key = Object.keys(prices).find(key => key.startsWith(dateOnly));

	return prices[iso] ?? prices[dateOnly] ?? (key ? prices[key] : undefined);
};
